import { Combobox } from '@/components/ui/combobox/combobox';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import useDeal from '@/hooks/use-deal';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores/deals';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { noop } from 'lodash';
import { STATUS_OPTIONS_MAP } from './deal-status-selector';

type DealStatusSelectorCombobox = {
  id: Deal['id'];
  shortId: Deal['short_id'];
  afterOnChange?: () => void;
  onSuccess?: (data: Deal, variables: { status: Deal['status'] }) => void;
  onError?: (error: Error, variables: { status: Deal['status'] }) => void;
};

export function DealStatusSelectorCombobox({
  id,
  shortId,
  afterOnChange,
  onSuccess,
  onError,
}: DealStatusSelectorCombobox) {
  const queryClient = useQueryClient();

  const { deal } = useDeal(shortId);

  const updateStatusMutation = useMutation({
    onMutate: async ({ status }) => {
      dealStore.updateStatus({ id, status });
    },
    mutationFn: async ({ status }: { status: Deal['status'] }) => {
      const response = await pdkAxios
        .post(`/deals/${id}/status`, { status })
        .catch((e) => {
          console.error('Error updating status', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      onError?.(error, variables);

      console.error('Error updating status', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', shortId] });
    },
  });

  const handleStatusChange = (newStatus: Deal['status']) => {
    if (deal?.status === 'done' || deal?.status === 'cancelled') {
      toast({
        title: 'Status cannot be changed',
        description: 'This deal is already finalized',
      });
      return;
    }

    updateStatusMutation.mutate({
      status: newStatus,
    });

    afterOnChange?.();
  };

  return (
    <Combobox
      endAdornment={<KeyboardKeyIcon>{HOTKEYS.DEAL_STATUS}</KeyboardKeyIcon>}
      placeholder="Set status..."
      // onChange={handleStatusChange}
      emptyMessage="No status found"
      options={STATUS_OPTIONS_MAP}
      // side={side}
      // align="start"
      onKeyDown={(e) => {
        const keyPressed = e.key;

        const option = STATUS_OPTIONS_MAP.find(
          (option) => option.hotkey === keyPressed,
        );

        if (option) {
          handleStatusChange(option.value);
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onChange={handleStatusChange}
      setOpen={noop}
    ></Combobox>
  );
}
